// library imports
import { useEffect, useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  DatePicker,
  Flex,
  Table,
  Button,
  Divider,
  Tooltip,
  Segmented,
} from "antd";
import {
  DownloadOutlined,
  InfoCircleOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
// style imports
import "../../styles/SavingReport/SavingReport.css";
// component imports
import SolarSaving from "../../components/saving_report/SolarSaving";
import MD from "../../components/saving_report/MD";
import EnergySaving from "../../components/saving_report/EnergySaving";
import SavingReportOverallSummary from "../../components/saving_report/SavingReportOverallSummary";
// hook imports
import useFetch from "../../hooks/useFetch";
// context imports
import { IndexContext } from "../../context/IndexContext";
// config imports
import { MICROGRID_SITE_IDS } from "../../configs/configs";

const { RangePicker } = DatePicker;

export default function SavingReportPage() {
  // fetch wrapper
  const [fetchWrapper] = useFetch();
  const navigate = useNavigate();

  // load index context
  const { siteSelected, siteList, notificationApi, setDisabledSiteSelection } =
    useContext(IndexContext);

  useEffect(() => {
    if (siteSelected && MICROGRID_SITE_IDS.includes(siteSelected)) {
      navigate("/Reports/SavingReportMicrogrid", { replace: true });
    }
  }, [siteSelected]);

  // loading states
  const [loading, setLoading] = useState(false); // loading for table
  const [downloadButtonLoading, setDownloadButtonLoading] = useState(false);
  const [
    loadingSavingReportOverallSummary,
    setLoadingSavingReportOverallSummary,
  ] = useState(false);
  const [loadingMd, setLoadingMd] = useState(false);

  // overall saving report summary table data
  const [
    overallSavingReportSummaryTableData,
    setOverallSavingReportSummaryTableData,
  ] = useState([]);
  // overall saving report summary chart data
  const [
    overallSavingReportSummaryChartData,
    setOverallSavingReportSummaryChartData,
  ] = useState([]);

  // summary of months of the year data
  const [tableData, setTableData] = useState([]);
  const [tableColumn, setTableColumn] = useState([
    {
      title: "Saving",
      dataIndex: "Saving",
      key: "Saving",
      width: 200,
    },
  ]);

  // selected year
  const [year, setYear] = useState(null);

  // site commissioning date
  const [siteCommissioningDate, setSiteCommissioningDate] = useState(null);

  // value of RangePicker when either one of the start date and end date has been selected
  const [dates, setDates] = useState(null);

  // value of RangePicker if both the start date and end date has been selected
  const [value, setValue] = useState(null);

  // committed search params — only updated when Search is clicked
  // shape: { year, value, reportMode, reportModeSite }
  const [searchParams, setSearchParams] = useState(null);

  // MD data
  const [mdSummaryData, setMdSummaryData] = useState([]);
  const [mdBreakdownData, setMdBreakdownData] = useState([]);
  const [totalMdSaving, setTotalMdSaving] = useState(null);
  const [totalMdKWSaving, setTotalMdKWSaving] = useState(null);
  const [mdDateBeforeBess, setMdDateBeforeBess] = useState(null);
  const [mdDateAfterBess, setMdDateAfterBess] = useState(null);

  // report mode: "ai_edr" | "ai_edr_pv" | "bess"
  const [showPvSaving, setShowPvSaving] = useState(null);
  const [reportMode, setReportMode] = useState(null);
  const [showAiEdr, setShowAiEdr] = useState(false);
  const [showBess, setShowBess] = useState(false);
  // tracks which site the report mode flags were loaded for
  const [reportModeSite, setReportModeSite] = useState(null);

  // Disable months that are not in the selected year
  const disabledDate = (current) => {
    if (!year) return true; // Disable all if year is not selected yet
    return current.year() !== year.year();
  };

  const onOpenChange = (open) => {
    if (open) {
      setDates([null, null]); // Reset dates to [null, null] when the picker is opened
    } else {
      setDates(null); // Clear the state completely when the picker is closed
    }
  };

  const onCalendarChange = (val) => {
    if (val[1] && !val[0]) {
      // If end date is selected first, set the start date to the Jan of the year
      const sameMonthStart = val[1].clone().startOf("month");
      setDates([sameMonthStart, val[1]]);
    } else {
      setDates(val); // Otherwise, just set the selected dates
    }
  };

  /* API to get whether site includes pv */
  function getAvailableReportMode() {
    const site = siteSelected;
    fetchWrapper({
      endpoint_url: "saving_report/getSiteAvailableReportMode",
      method: "GET",
      set_content_type_header: false,
      params: {
        site_id: site,
      },

      onSuccess: (response) => {
        setShowAiEdr(response.data.show_ai_edr);
        setShowPvSaving(response.data.show_pv);
        setShowBess(response.data.show_bess);
        setReportMode(
          response.data.show_ai_edr
            ? "ai_edr"
            : response.data.show_bess
              ? "bess"
              : null,
        );
        setReportModeSite(site);
      },
    });
  }

  /* API to get site commissioning date */
  function getSiteCommissioningDate() {
    fetchWrapper({
      endpoint_url: "common/getSiteCommissioningDate",
      method: "GET",
      set_content_type_header: false,
      params: {
        site_id: siteSelected,
      },

      onSuccess: (response) => {
        setSiteCommissioningDate(response.data.site_commissioning_date);

        // when site commissioning date changes, need to update year, dates and date values
        setYear(
          dayjs(
            Math.max(
              dayjs().startOf("year").valueOf(),
              dayjs(
                response.data.site_commissioning_date,
                "YYYY-MM-DD",
              ).valueOf(),
            ),
          ),
        );

        setDates([
          dayjs(
            Math.max(
              dayjs().startOf("year").valueOf(),
              dayjs(
                response.data.site_commissioning_date,
                "YYYY-MM-DD",
              ).valueOf(),
            ),
          ),
          dayjs(),
        ]);

        setValue([
          dayjs(
            Math.max(
              dayjs().startOf("year").valueOf(),
              dayjs(
                response.data.site_commissioning_date,
                "YYYY-MM-DD",
              ).valueOf(),
            ),
          ),
          dayjs(),
        ]);
      },
      onResponseError: (response) => {
        notificationApi.error({
          message: "Failed to get site commissioning date.",
          description: response.statusText,
        });
      },
    });
  }

  /** API to get saving report summary */
  function getSavingReportSummary() {
    setLoading(true);

    fetchWrapper({
      endpoint_url: "saving_report/getSavingReportSummary",
      method: "GET",
      set_content_type_header: false,
      params: {
        site_id: siteSelected,
        start_date: value[0].startOf("month").format("YYYY-MM-DD"),
        end_date: value[1].endOf("month").format("YYYY-MM-DD"),
        include_pv: reportMode === "ai_edr",
      },

      onSuccess: (response) => {
        setTableColumn(response.data.table_column);
        setTableData(response.data.table_data);
      },
      onResponseError: (response) => {
        notificationApi.error({
          message: "Failed to get saving report summary.",
          description: response.statusText,
        });
      },

      onFinish: () => {
        setLoading(false);
      },
    });
  }

  /** API to download saving report */
  function downloadSavingReport() {
    setDownloadButtonLoading(true);

    fetchWrapper({
      endpoint_url: "saving_report/downloadSavingReport",
      method: "POST",
      json_output: false,
      body: {
        site_id: siteSelected,
        year: searchParams.year.year(),
        start_date: searchParams.value[0].startOf("month").format("YYYY-MM-DD"),
        end_date: searchParams.value[1].endOf("month").format("YYYY-MM-DD"),
        saving_report_summary: JSON.stringify(tableData),
        saving_report_overall_summary_table_data: JSON.stringify(
          overallSavingReportSummaryTableData,
        ),
        saving_report_overall_summary_chart_data: JSON.stringify(
          overallSavingReportSummaryChartData,
        ),
        include_pv: searchParams.reportMode === "ai_edr",
      },

      onSuccess: (response) => {
        if (response.headers.get("content-type") !== "application/json") {
          // no issue with the response. returned a spreadsheet
          response.blob().then((blob) => {
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            const startMonth = searchParams.value[0].startOf("month");
            const endMonth = searchParams.value[1].endOf("month");
            const isSameMonth = startMonth.isSame(endMonth, "month");
            a.download = isSameMonth
              ? `SavingReport_${startMonth.format("MMM")}_${startMonth.format("YYYY")}_${siteList.find((site) => site.value === siteSelected)?.label}.pdf`
              : `SavingReport_${startMonth.format("MMM")}_${endMonth.format("MMM")}_${startMonth.format("YYYY")}_${siteList.find((site) => site.value === siteSelected)?.label}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
          });
        }
      },
      onResponseError: (response) => {
        notificationApi.error({
          message: "Failed to download file",
        });
      },

      onFinish: () => {
        setDownloadButtonLoading(false);
      },
    });
  }

  useEffect(() => {
    setReportMode(null);
    setShowPvSaving(null);
    setShowAiEdr(false);
    setShowBess(false);
    setReportModeSite(null);
    setValue(null);
    setSearchParams(null);

    // clear data when change site
    setMdSummaryData([]);
    setMdBreakdownData([]);
    setTotalMdSaving(null);
    setTotalMdKWSaving(null);
    setMdDateBeforeBess(null);
    setMdDateAfterBess(null);
    setOverallSavingReportSummaryTableData([]);
    setOverallSavingReportSummaryChartData([]);
    setTableData([]);

    if (siteSelected) {
      getSiteCommissioningDate();
      getAvailableReportMode();
    }
  }, [siteSelected]);

  useEffect(() => {
    // clear data when change report mode
    setMdSummaryData([]);
    setMdBreakdownData([]);
    setTotalMdSaving(null);
    setTotalMdKWSaving(null);
    setMdDateBeforeBess(null);
    setMdDateAfterBess(null);
    setOverallSavingReportSummaryTableData([]);
    setOverallSavingReportSummaryChartData([]);
    setTableData([]);
  }, [reportMode]);

  function handleSearch() {
    if (!siteSelected) {
      notificationApi.error({ message: "Please select a site first." });
      return;
    }
    if (!value || !value[0] || !value[1]) {
      notificationApi.error({ message: "Please select a month range." });
      return;
    }
    if (reportModeSite !== siteSelected) {
      notificationApi.error({
        message: "Page is still loading. Please try again.",
      });
      return;
    }
    const params = {
      year,
      value,
      reportMode,
      reportModeSite,
    };
    setSearchParams(params);
    getSavingReportSummary();
  }

  useEffect(() => {
    setDisabledSiteSelection(
      loading ||
        downloadButtonLoading ||
        loadingSavingReportOverallSummary ||
        loadingMd,
    );
  }, [
    loading,
    downloadButtonLoading,
    loadingSavingReportOverallSummary,
    loadingMd,
  ]);

  const isControlsDisabled =
    loading ||
    downloadButtonLoading ||
    loadingSavingReportOverallSummary ||
    loadingMd;

  const segmentedOptions = [
    showAiEdr ? { label: "AIE-DR", value: "ai_edr" } : null,
    showBess ? { label: "Exclude PV", value: "bess" } : null,
  ].filter(Boolean);

  return (
    <div
      style={{
        margin: "10px",
        height: "90%",
      }}
      className="saving-report"
    >
      <Card
        styles={{
          body: {
            padding: 0,
          },
        }}
        title={
          <Flex justify="space-between" align="center" wrap={true} gap={8}>
            <Flex gap={8} align="center">
              <p>Saving Report</p>
              <Tooltip title="Click Search button after selecting year and month">
                <InfoCircleOutlined />
              </Tooltip>
            </Flex>

            <Flex gap={8}>
              <Segmented
                options={segmentedOptions}
                value={reportMode}
                disabled={isControlsDisabled || reportMode === null}
                onChange={(val) => setReportMode(val)}
              />
              <Button
                disabled={
                  loading ||
                  loadingSavingReportOverallSummary ||
                  searchParams === null
                }
                loading={downloadButtonLoading}
                icon={<DownloadOutlined />}
                onClick={() => {
                  if (value) {
                    downloadSavingReport();
                  }
                }}
              />
            </Flex>
          </Flex>
        }
      >
        {/* Scrollable content */}
        <div
          style={{
            overflowY: "auto",
            maxHeight: "calc(100vh - 200px)",
            padding: "0 24px",
          }}
        >
          <Flex align="center" style={{ marginTop: "12px" }} gap={8}>
            <div className="mr-2">Year: </div>
            <DatePicker
              allowClear={false}
              disabled={
                siteCommissioningDate === null ||
                loadingSavingReportOverallSummary
              }
              picker="year"
              value={year}
              onChange={(value) => {
                setYear(value);

                if (siteCommissioningDate) {
                  if (value.startOf("year") < dayjs().startOf("year")) {
                    setDates([
                      dayjs(
                        Math.max(
                          value.startOf("year").valueOf(),
                          dayjs(siteCommissioningDate, "YYYY-MM-DD").valueOf(),
                        ),
                      ),
                      value.endOf("year"),
                    ]);

                    setValue([
                      dayjs(
                        Math.max(
                          value.startOf("year").valueOf(),
                          dayjs(siteCommissioningDate, "YYYY-MM-DD").valueOf(),
                        ),
                      ),
                      value.endOf("year"),
                    ]);
                  } else {
                    setDates([value.startOf("year"), dayjs()]);
                    setValue([value.startOf("year"), dayjs()]);
                  }
                }
              }}
              maxDate={dayjs()}
              minDate={dayjs(siteCommissioningDate)}
            />

            <Flex align="center">
              <Flex>
                <div className="mr-1">Month:</div>
                <Tooltip title="You need to select year first before selecting month if you wish to view data of another year.">
                  <InfoCircleOutlined className="mr-2" />
                </Tooltip>
              </Flex>
              <RangePicker
                allowClear={false}
                disabled={loading} // when getting table data, disable month selection
                maxDate={dayjs()}
                minDate={dayjs(siteCommissioningDate)}
                picker="month"
                defaultValue={dates}
                value={dates || value}
                disabledDate={disabledDate} // Apply the disabledDate function
                onCalendarChange={onCalendarChange} // Update selected dates
                onChange={(val) => {
                  setValue(val);
                }} // Update final date value
                onOpenChange={onOpenChange} // Reset when the picker opens
              />
              <Button
                type="primary"
                icon={<SearchOutlined />}
                loading={loading}
                disabled={isControlsDisabled || !siteSelected}
                onClick={handleSearch}
                style={{ marginLeft: 8 }}
              >
                Search
              </Button>
            </Flex>
          </Flex>

          <SavingReportOverallSummary
            year={searchParams?.year}
            dates={searchParams?.value}
            loading={loadingSavingReportOverallSummary}
            setLoading={setLoadingSavingReportOverallSummary}
            tableData={overallSavingReportSummaryTableData}
            setTableData={setOverallSavingReportSummaryTableData}
            chartData={overallSavingReportSummaryChartData}
            setChartData={setOverallSavingReportSummaryChartData}
            includePv={
              searchParams?.reportMode
                ? searchParams.reportMode === "ai_edr"
                : searchParams?.reportMode
            }
            reportModeSite={searchParams?.reportModeSite}
          />

          <Divider style={{ borderColor: "lightgray" }} />

          <Table
            style={{ marginBottom: "12px" }}
            columns={tableColumn}
            dataSource={tableData}
            pagination={false}
            loading={loading}
            size="small"
          />

          {showPvSaving && searchParams?.reportModeSite === siteSelected && (
            <Divider style={{ borderColor: "lightgray" }} />
          )}
          {showPvSaving && searchParams?.reportModeSite === siteSelected && (
            <SolarSaving
              year={searchParams?.year}
              siteCommissioningDate={siteCommissioningDate}
              startDateOri={
                searchParams?.value
                  ? searchParams.value[0].startOf("month").format("YYYY-MM-DD")
                  : dayjs().startOf("month").format("YYYY-MM-DD")
              }
              endDateOri={
                searchParams?.value
                  ? searchParams.value[1].endOf("month").format("YYYY-MM-DD")
                  : dayjs().endOf("month").format("YYYY-MM-DD")
              }
              showPvSaving={showPvSaving}
              reportModeSite={searchParams?.reportModeSite}
              reportMode={reportMode}
              committedReportMode={searchParams?.reportMode}
            />
          )}
          <Divider style={{ borderColor: "lightgray" }} />

          <MD
            searchParams={searchParams}
            year={searchParams?.year}
            siteCommissioningDate={siteCommissioningDate}
            mdDateAfterBess={mdDateAfterBess}
            setMdDateAfterBess={setMdDateAfterBess}
            mdDateBeforeBess={mdDateBeforeBess}
            setMdDateBeforeBess={setMdDateBeforeBess}
            totalSaving={totalMdSaving}
            setTotalSaving={setTotalMdSaving}
            totalMdKWSaving={totalMdKWSaving}
            setTotalMdKWSaving={setTotalMdKWSaving}
            mdSummaryData={mdSummaryData}
            setMdSummaryData={setMdSummaryData}
            mdBreakdownData={mdBreakdownData}
            setMdBreakdownData={setMdBreakdownData}
            startDateOri={
              searchParams?.value
                ? searchParams.value[0].startOf("month").format("YYYY-MM-DD")
                : dayjs().startOf("month").format("YYYY-MM-DD")
            }
            endDateOri={
              searchParams?.value
                ? searchParams.value[1].startOf("month").format("YYYY-MM-DD")
                : dayjs().endOf("month").format("YYYY-MM-DD")
            }
            includePv={
              searchParams?.reportMode
                ? searchParams.reportMode === "ai_edr"
                : searchParams?.reportMode
            }
            reportModeSite={searchParams?.reportModeSite}
            loading={loadingMd}
            setLoading={setLoadingMd}
            reportMode={reportMode}
          />

          <Divider style={{ borderColor: "lightgray" }} />

          <EnergySaving
            searchParams={searchParams}
            year={searchParams?.year}
            siteCommissioningDate={siteCommissioningDate}
            startDateOri={
              searchParams?.value
                ? searchParams.value[0].startOf("month").format("YYYY-MM-DD")
                : dayjs().startOf("month").format("YYYY-MM-DD")
            }
            endDateOri={
              searchParams?.value
                ? searchParams.value[1].endOf("month").format("YYYY-MM-DD")
                : dayjs().endOf("month").format("YYYY-MM-DD")
            }
            reportModeSite={searchParams?.reportModeSite}
            reportMode={reportMode}
            committedReportMode={searchParams?.reportMode}
          />
        </div>
      </Card>
    </div>
  );
}
